'use client';

import type { YouTubeEmbedBlockDto } from '@/lib/sanity/dto';
import { useLightbox, useSiteSettings } from '@/components/providers';
import { cn } from '@/lib/tailwind';
import { LINK_VARIANT_MAP } from '@/lib/ui/tailwind-mapping.constants';
import { resolveImageAlt, sanityImage } from '@/lib/sanity/image';
import { YouTubeEmbedAndConsent } from './YouTubeEmbedAndConsent';

type Props = {
    block: YouTubeEmbedBlockDto;
};

const DEFAULT_CONSENT_TEXT =
    'Durch das Ansehen des Videos erklären Sie sich damit einverstanden, dass Ihre Daten an YouTube übertragen werden und dass Sie die [privacy] gelesen haben.';
const DEFAULT_CONSENT_BUTTON_TEXT = 'Video abspielen';

function getYouTubeEmbedUrl(url: string): string {
    try {
        const parsed = new URL(url);
        let videoId: string | null = null;

        if (parsed.hostname.includes('youtu.be')) {
            videoId = parsed.pathname.slice(1);
        } else {
            videoId = parsed.searchParams.get('v');
        }

        if (!videoId) return url;
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    } catch {
        return url;
    }
}

export function YouTubeEmbed({ block }: Props) {
    const { url, outputAsLink, linkText, linkType, showVideoIcon, placeholderImage } = block;
    const { openLightbox } = useLightbox();
    const { privacySettings } = useSiteSettings();

    if (!url) return null;

    const placeholderImageUrl = sanityImage.gallery(placeholderImage ?? undefined);
    const placeholderImageAlt = resolveImageAlt(placeholderImage, linkText ?? '');
    const consentText = privacySettings?.consentText ?? DEFAULT_CONSENT_TEXT;
    const consentButtonText = privacySettings?.consentButtonText ?? DEFAULT_CONSENT_BUTTON_TEXT;
    const privacyPageSlug = privacySettings?.privacyPage?.slug;
    const embedUrl = getYouTubeEmbedUrl(url);
    const title = linkText ?? 'YouTube Video';

    if (!outputAsLink) {
        return (
            <YouTubeEmbedAndConsent
                src={embedUrl}
                title={title}
                placeholderImageUrl={placeholderImageUrl}
                placeholderImageAlt={placeholderImageAlt}
                consentText={consentText}
                consentButtonText={consentButtonText}
                privacyPageSlug={privacyPageSlug}
            />
        );
    }

    const className = LINK_VARIANT_MAP[linkType ?? 'pt-text-link'] ?? '';

    function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
        e.preventDefault();
        openLightbox({
            slides: [
                {
                    type: 'video',
                    src: embedUrl,
                    width: 1280,
                    height: 720,
                    sources: [{ src: embedUrl, type: 'video/mp4' }],
                } as never,
            ],
            options: {
                render: {
                    slide: ({ slide }: { slide: { src: string } }) => (
                        <div className="relative w-full h-full flex items-center justify-center">
                            <YouTubeEmbedAndConsent
                                src={slide.src}
                                title={title}
                                placeholderImageUrl={placeholderImageUrl}
                                placeholderImageAlt={placeholderImageAlt}
                                consentText={consentText}
                                consentButtonText={consentButtonText}
                                privacyPageSlug={privacyPageSlug}
                                className="max-w-page-xs"
                            />
                        </div>
                    ),
                },
            } as never,
        });
    }

    return (
        <div className="prose">
            <a
                href={url}
                onClick={handleClick}
                className={cn(className, showVideoIcon ? 'inline-flex gap-2' : undefined)}
            >
                {showVideoIcon && (
                    <span className={'w-auto'}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="size-5"
                            aria-hidden="true"
                        >
                            <path
                                fillRule="evenodd"
                                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm14.024-.983a1.125 1.125 0 0 1 0 1.966l-5.603 3.113A1.125 1.125 0 0 1 9 15.113V8.887c0-.857.921-1.4 1.671-.983l5.603 3.113Z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </span>
                )}
                {linkText ?? url}
            </a>
        </div>
    );
}
